class CreativityCommunity extends HTMLElement {
	constructor() {
		super();
		this.defaultData = {
			title: 'Your Creativity, Our Community',
			description1:
				'In our community, share your creations, get feedback, and feel the love. Code27 supports originality—we love what you love, and we make it easy for you to create.',
			description2:
				"Our Discord is home to creators like you—a space to share, learn, and grow together. From your first sketch to your final render, we're here to cheer you on through every step.",
			buttonText: 'Join Our Discord Group',
			buttonLink: '#',
			desktopImage:
				'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=600&h=400&fit=crop&crop=center',
			mobileImage:
				'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&h=400&fit=crop&crop=center',
			socialLinks: {
				tiktok: 'https://tiktok.com',
				instagram: 'https://instagram.com',
				facebook: 'https://facebook.com',
				youtube: 'https://youtube.com',
				twitter: 'https://twitter.com'
			}
		};
	}

	connectedCallback() {
		this.render();
	}

	static get observedAttributes() {
		return ['data'];
	}

	attributeChangedCallback() {
		this.render();
	}

	getData() {
		const dataAttr = this.getAttribute('data');
		if (dataAttr) {
			try {
				// 使用decodeURIComponent处理从Shopify传来的转义数据
				const decodedData = decodeURIComponent(dataAttr);
				const parsedData = JSON.parse(decodedData);
				return {...this.defaultData, ...parsedData};
			} catch (e) {
				console.error('Invalid JSON in data attribute:', e);
				return this.defaultData;
			}
		}
		return this.defaultData;
	}

	render() {
		const data = this.getData();

		this.innerHTML = `
      <div class="relative overflow-hidden">
        <div class="relative z-10 p-6 md:p-12 max-w-[1200px] mx-auto">
          <!-- Title -->
          <h1 class="text-3xl md:text-5xl lg:text-6xl font-bold text-white text-center mb-8 md:mb-12 leading-tight">
            ${data.title}
          </h1>

          <!-- Images Section -->
          <div class="grid grid-cols-1 lg:grid-cols-[minmax(0,auto)_minmax(0,auto)] gap-6 md:gap-8 mb-8 md:mb-12">
            <!-- Desktop Image -->
            <div class="relative group">
              <img
                src="${data.desktopImage || this.defaultData.desktopImage}"
                alt="Desktop Application Interface"
                class="w-full lg:w-auto lg:h-[330px] object-cover rounded-lg transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
              <div class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-lg"></div>
            </div>

            <!-- Mobile Image -->
            <div class="relative group">
              <img
                src="${data.mobileImage || this.defaultData.mobileImage}"
                alt="Mobile Application Interface"
                class="ml-auto w-full lg:w-auto lg:h-[330px] object-cover rounded-lg transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
              <div class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-lg"></div>
            </div>
          </div>

          <!-- Content Section -->
          <div class="flex flex-col lg:flex-row items-center lg:items-end justify-between gap-8">
            <!-- Text Content -->
            <div class="flex-1 space-y-6">
              <p class="text-white text-2xl md:text-2xl leading-relaxed max-w-5xl">
                ${data.description1}
              </p>

              <p class="text-white text-2xl md:text-2xl leading-relaxed max-w-5xl">
                ${data.description2}
              </p>
            </div>

            <!-- Right Side: Social Icons and Button -->
            <div class="flex flex-col items-center lg:items-end gap-6">
              <!-- Social Icons -->
              <div class="flex gap-12">
                <a href="${
									data.socialLinks.tiktok
								}" class="text-white hover:text-pink-400 transition-colors duration-200" aria-label="TikTok" target="_blank" rel="noopener">
                  <svg class="size-10" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>

                <a href="${
									data.socialLinks.instagram
								}" class="text-white hover:text-pink-400 transition-colors duration-200" aria-label="Instagram" target="_blank" rel="noopener">
                  <svg class="size-10" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>

                <a href="${
									data.socialLinks.facebook
								}" class="text-white hover:text-blue-400 transition-colors duration-200" aria-label="Facebook" target="_blank" rel="noopener">
                  <svg class="size-10" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>

                <a href="${
									data.socialLinks.youtube
								}" class="text-white hover:text-red-400 transition-colors duration-200" aria-label="YouTube" target="_blank" rel="noopener">
                  <svg class="size-10" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>

                <a href="${
									data.socialLinks.twitter
								}" class="text-white hover:text-blue-400 transition-colors duration-200" aria-label="Twitter" target="_blank" rel="noopener">
                  <svg class="size-10" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
              </div>

              <!-- Join Button -->
              <div class="relative mt-4 w-full">
                  <!-- 外层发光效果 -->
                  <div class="absolute inset-0 bg-gradient-to-r from-purple-500 to-cyan-400 rounded-2xl blur-sm opacity-75 animate-glow"> </div>

                <!-- 按钮主体 -->
                <a
                  href="${data.buttonLink || '#'}"
                  class="bg-origin-border border-[rgba(255,255,255,0.4)] border-solid border-[3px] relative px-12 py-2 bg-gradient-to-r from-purple-500 to-blue-600 text-white font-bold text-xl rounded-2xl shadow-lg transform transition-all duration-200 hover:scale-105 hover:shadow-2xl active:scale-95 w-full flex justify-center items-center"
                  ${
										data.buttonLink && data.buttonLink !== '#'
											? 'target="_blank" rel="noopener"'
											: ''
									}
                >
                  ${data.buttonText}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
	}
}

// Register the custom element
customElements.define('creativity-community', CreativityCommunity);
